import { useElementExpressionVariablesById } from '@client/hooks/use-element-expression-variables.hooks';
import {
  useBuildingLifetime,
  useProjectBuildingGFA,
} from '@client/store/project';
import { useSelectedVersion, useSelectedVersionId } from '@client/store/ui';
import { usePvgisEnvironmentHourData } from '@client/store/pvgis';
import { getBuildingEnvelopeElements } from '@shared/helpers/energy/building-envelope.helpers';
import {
  getDesignOutdoorTemp,
  getGeographicalAdjustmentFactor,
} from '@shared/helpers/energy/city-climate.helpers';
import {
  getEnergyConsumptionRecordFromBuildingVersion,
  getPrimaryEnergyValues,
} from '@shared/helpers/energy/energy-class';
import {
  EnergyCarrierMix,
  EnergyConsumer,
  EnergyConsumptionRecord,
  EnergyMixUnit,
} from '@shared/helpers/energy/energy-class.model';
import {
  getBuildingHeatingDemand,
  getHeatedArea,
} from '@shared/helpers/energy/energy.helpers';
import { getDegreeHoursFromCoordinate } from '@shared/helpers/energy/temperature.helpers';
import { getAverageThermalTransmittanceFromBuildingEnvelope } from '@shared/helpers/energy/thermal-transmittance.helpers';
import { sumObjects } from '@shared/helpers/math.helpers';
import { getId } from '@shared/helpers/object.helpers';
import { OneOfElements } from '@shared/models/project.model';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { useCallback, useMemo } from 'react';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';
import { IProposal } from '@shared/models/proposals.model';
import { getProposalsInVersion } from '@shared/helpers/proposal.helpers';
import {
  useBuildingDesign,
  useBuildingLocation,
} from '@features/design/hooks/design.hooks';
import { useDesignNia } from '@features/design/hooks/activities.hooks';
import { useProposalResultsRecord } from '@features/proposals/use-proposals.hooks';
import { guards } from '@shared/helpers/typeguards.helpers';

/**
 * Get the the yearly energy mix per energy consumer (heating, cooling, tapwater, building)
 * @returns The yearly energy consumption record
 */
export const useYearlyEnergyConsumptionRecord = (
  unit?: EnergyMixUnit,
  proposal?: ItemOrItemId<IProposal | IProposal>,
): EnergyConsumptionRecord => {
  const version = useSelectedVersion();
  const buildingLifetime = useBuildingLifetime();

  return useMemo(() => {
    return getEnergyConsumptionRecordFromBuildingVersion(
      version,
      buildingLifetime,
      unit,
      proposal,
    );
  }, [version, buildingLifetime, unit, proposal]);
};

/**
 * Get the yearly energy consumption per proposal (kWh/co2e/sek)
 * Returns a record keyed by proposal.id
 */
export const useYearlyEnergyConsumptionRecordByProposal = (
  unit?: EnergyMixUnit,
): Record<string, EnergyConsumptionRecord> => {
  const version = useSelectedVersion();
  const buildingLifetime = useBuildingLifetime();

  return useMemo(() => {
    if (!version) return {};
    const proposals = getProposalsInVersion(version);
    const result: Record<string, EnergyConsumptionRecord> = {};
    for (const proposal of proposals) {
      result[proposal.id] = getEnergyConsumptionRecordFromBuildingVersion(
        version,
        buildingLifetime,
        unit,
        proposal,
      );
    }
    return result;
  }, [version, buildingLifetime, unit]);
};

export const useGetYearlyEnergyConsumptionRecord = (): ((
  proposal: ItemOrItemId<IProposal | IProposal>,
  unit?: EnergyMixUnit,
) => EnergyConsumptionRecord) => {
  const version = useSelectedVersion();
  const buildingLifetime = useBuildingLifetime();
  return useCallback(
    (proposal, unit) =>
      getEnergyConsumptionRecordFromBuildingVersion(
        version,
        buildingLifetime,
        unit,
        proposal,
      ),
    [version, buildingLifetime],
  );
};

/**
 * Get the yearly energy consumption per carrier (oil, gas, electricity, bio, district heating, district cooling)
 * @returns The yearly energy carrier mix
 */
export const useYearlyEnergyCarrierMix = (): EnergyCarrierMix => {
  const energyConsumptionRecord = useYearlyEnergyConsumptionRecord();
  return useMemo(
    () => sumObjects(energyConsumptionRecord),
    [energyConsumptionRecord],
  );
};

export const usePrimaryEnergyValues = (): Record<EnergyConsumer, number> => {
  const heatedArea = useHeatedArea();
  const energyConsumptionRecord = useYearlyEnergyConsumptionRecord();
  const location = useBuildingLocation();
  const geoFactor = getGeographicalAdjustmentFactor(location);
  return useMemo(
    () =>
      getPrimaryEnergyValues(energyConsumptionRecord, heatedArea, geoFactor),
    [energyConsumptionRecord, heatedArea, geoFactor],
  );
};

export const useAverageThermalTransmittance = () => {
  const version = useSelectedVersion();
  return useMemo(
    () =>
      version ? getAverageThermalTransmittanceFromBuildingEnvelope(version) : 0,
    [version],
  );
};

export const useDegreeHours = () => {
  const location = useBuildingLocation();
  return useMemo(() => getDegreeHoursFromCoordinate(location), [location]);
};

export const useDesignOutdoorTemp = () => {
  const location = useBuildingLocation();
  return useMemo(() => getDesignOutdoorTemp(location), [location]);
};

export const useBuildingHeatingDemand = () => {
  const version = useSelectedVersion();
  const environmentHourData = usePvgisEnvironmentHourData();
  const heatedArea = useHeatedArea();
  const design = useBuildingDesign();

  return useMemo(() => {
    // getBuildingHeatingDemand is cached — shared with the enrich pipeline
    return getBuildingHeatingDemand(version, {
      environmentHours: environmentHourData ?? [],
      heatedArea,
      design,
    });
  }, [version, environmentHourData, heatedArea, design]);
};

export const useBuildingEnvelopeElements = () => {
  const version = useSelectedVersion();
  return useMemo(
    () => (version ? getBuildingEnvelopeElements(version) : []),
    [version],
  );
};

/**
 * Get the heated area (atemp) for an element. Can be overriden in every element by setting atemp property
 * @param element - The element to get the heated area for
 * @returns The heated area
 */
export const useHeatedArea = (element?: ItemOrItemId<OneOfElements>) => {
  const designGfa = useProjectBuildingGFA();
  const nia = useDesignNia();
  const versionId = useSelectedVersionId();
  const id = element ? getId(element) : versionId;
  const variables = useElementExpressionVariablesById(id);
  return useMemo(
    () =>
      variables[EXPRESSION_VARIABLE_NAME.HEATED_AREA] ??
      (nia || getHeatedArea(designGfa)),
    [variables, nia, designGfa],
  );
};

/**
 * Check if selected building version has any elements with B6 energy consumption (drift).
 * @returns true if there are any energy feature elements
 */
/** Version-level B6 rollup lives in active proposal's resultsRecord[version.id] */
export const useHasB6EnergyConsumption = (): boolean => {
  const versionId = useSelectedVersionId();
  const record = useProposalResultsRecord(undefined, {
    applyLifecycleFilter: false,
  });
  const co2eB6 = versionId && record ? record[versionId]?.co2e_B6 : undefined;
  return guards.is.number(co2eB6) && co2eB6 > 0;
};
